import React, { useContext } from "react";
import { formatMonto } from "../../utils";
import useProyecto from "../../hooks/useProyecto";
import { CapacitorContext } from "../../context/CapacitorContext";
import {
  getConceptosKeys,
  getSumConcepto,
  isAirConditioner,
  getEffectiveDefaultType,
  REQUIRED_DEFAULT_TYPES,
} from "../../utils/conceptos";
import { EstimacionVersionesContext } from "../../context/EstimacionVersionesContext";
import { AuthContext } from "../../context/AuthContext";

const ensureDefaultConceptoMedicions = (conceptoMedicions, keys, userDefaultType) => {
  if (!conceptoMedicions || conceptoMedicions.length === 0) {
    return [];
  }

  const requiredTypes = REQUIRED_DEFAULT_TYPES; // [6, 3, 5]
  const firstConceptoMedicion = conceptoMedicions[0];

  // Group the existing ConceptoMedicions into the three recognized columns.
  // Anything that isn't Supervisor(3)/Obras(5) folds into Contratista(6), so an
  // upload made by an admin lands in the Contratista column instead of adding a
  // stray fourth group that pushes every value out of alignment.
  const existingByType = new Map();
  conceptoMedicions.forEach((cm) => {
    const effectiveType = getEffectiveDefaultType(cm.Usuario?.defaultType);
    // Keep the first medición seen for each column (an explicit role wins over a
    // later folded one only by insertion order, which is fine for a single set).
    if (!existingByType.has(effectiveType)) {
      existingByType.set(effectiveType, cm);
    }
  });

  const effectiveUserType = getEffectiveDefaultType(userDefaultType);

  // Always return exactly the three required columns, in order, so the row lines
  // up with the CONTRATISTA / SUPERVISOR / OBRAS headers.
  return requiredTypes.map((defaultType) => {
    if (existingByType.has(defaultType)) {
      return existingByType.get(defaultType);
    }
    // Create placeholder: if the missing column belongs to the current user,
    // clone values from the first ConceptoMedicion; otherwise leave it empty.
    const baseValues = {};
    if (defaultType === effectiveUserType) {
      keys.forEach((k) => {
        baseValues[k] = firstConceptoMedicion[k];
      });
      // Special case for semip is computed at render time from ancho/alto
      baseValues.ancho = firstConceptoMedicion.ancho;
      baseValues.alto = firstConceptoMedicion.alto;
    } else {
      keys.forEach((k) => {
        baseValues[k] = "";
      });
      baseValues.ancho = "";
      baseValues.alto = "";
    }

    return {
      ...firstConceptoMedicion,
      ...baseValues,
      idConceptoMedicion: `placeholder-${defaultType}`,
      Usuario: {
        ...(firstConceptoMedicion.Usuario || {}),
        defaultType,
      },
    };
  });
};

const ConceptoGenerador = ({
  concepto,
  disabled,
  handleEdit,
  handleComments,
}) => {
  const { estimacionversion } = useContext(EstimacionVersionesContext);
  const { platform } = useContext(CapacitorContext);
  const { permiso } = useProyecto();
  const { user } = useContext(AuthContext);

  const renderCampos = () => {
    const keys = getConceptosKeys(concepto);
    // Already ordered as CONTRATISTA / SUPERVISOR / OBRAS.
    const sortedConceptoMedicions = ensureDefaultConceptoMedicions(
      concepto.ConceptoMedicions,
      keys,
      user?.defaultType
    );

    const hasValidValues = (cm) => {
      // All required keys must be numeric to show total
      return keys.every((k) => {
        if (k === "semip") {
          const a = parseFloat(cm.ancho);
          const b = parseFloat(cm.alto);
          return !isNaN(a) && !isNaN(b);
        }
        const v = parseFloat(cm[k]);
        return !isNaN(v);
      });
    };

    return sortedConceptoMedicions.map((conceptoMedicion) => {
      return [
        ...keys.map((key) => {
          let value = "";
          if (key === "semip") {
            const a = parseFloat(conceptoMedicion.ancho);
            const b = parseFloat(conceptoMedicion.alto);
            value = !isNaN(a) && !isNaN(b) ? a + b : "";
          } else {
            value = conceptoMedicion[key] ?? "";
          }
          return (
            <td className="border" key={`${key}-${conceptoMedicion.idConceptoMedicion}`}>
              {value}
            </td>
          );
        }),
        <td className="border" key={`total-${conceptoMedicion.idConceptoMedicion}`}>
          {hasValidValues(conceptoMedicion)
            ? renderTotal({ ...conceptoMedicion, unidad: concepto.unidad })
            : ""}
        </td>,
      ];
    });
  };

  const renderEdit = () => {
    const estado =
      estimacionversion && estimacionversion !== null
        ? estimacionversion.estado
        : "";
    if (
      (permiso === "contratista" && estado === "creada") ||
      (permiso === "supervisor" && estado === "enviada") ||
      (permiso === "obras" && estado === "conciliada")
    ) {
      return (
        <button
          disabled={disabled}
          className="btn btn-sm btn-outline-dark"
          onClick={() => handleEdit(concepto.idConceptoUbicacion)}
        >
          <i className="fa fa-edit" />
        </button>
      );
    }
  };

  const renderTotal = (concepto) => {
    const total = getSumConcepto(concepto);
    return formatMonto(total);
  };

  return (
    <tr className="border position-relative" style={{ overflowX: "scroll" }}>
      {platform !== "web" && (
        <td className="border">
          {renderEdit()}
          <button
            onClick={() => {
              handleComments(concepto);
            }}
            className="btn btn-sm btn-outline-primary mt-2 d-block"
          >
            <i className="fa fa-comment" />
          </button>
        </td>
      )}
      <td className="border">{concepto.eje}</td>
      {!isAirConditioner(concepto) && (
        <>
          <td className="border">{concepto.tramo}</td>
          <td className="border">{concepto.tipo}</td>
          <td className="border">{concepto.nivel}</td>
        </>
      )}
      {renderCampos()}
      {platform === "web" && (
        <td className="border">
          {renderEdit()}
          <button
            onClick={() => {
              handleComments(concepto);
            }}
            className="btn btn-sm btn-outline-primary ms-2"
          >
            <i className="fa fa-comment" />
          </button>
        </td>
      )}
    </tr>
  );
};

export default ConceptoGenerador;
